import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useQueryClient } from 'react-query';
import { useLumaBotFlowRemoveOneV2 } from 'ui-model';
import { snackbarError, snackbarSuccess } from 'actions/snackbarMessage';

import Button from '../../../Button';
import DialogActions from '../../../DialogActions';
import DialogTitle from '../../../DialogTitle';
import DialogContent from '../../../DialogContent';
import Dialog, { DialogProps } from '../../../Dialog';
import Typography from '../../../Typography';

import messages from './DialogDelete.messages';

export interface IDialogDelete extends Omit<DialogProps, 'classes' | 'onClose' | 'onSubmit'> {
  setDialogOpen: (value: boolean) => void;
  flowId: string;
}

const DeleteDialog: React.FunctionComponent<IDialogDelete> = ({ setDialogOpen, open: isDialogOpen, flowId }) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { mutate: deleteFlow } = useLumaBotFlowRemoveOneV2({
    onSuccess: () => {
      queryClient.invalidateQueries('sessions');
      dispatch(snackbarSuccess(formatMessage(messages.success)));
    },
    onError: () => {
      dispatch(snackbarError(formatMessage(messages.error)));
    },
  });

  const handleDeleteFlow = React.useCallback(() => {
    setDialogOpen(false);
    deleteFlow({ id: flowId });
  }, [setDialogOpen, deleteFlow, flowId]);

  return (
    <Dialog onClose={() => setDialogOpen(false)} open={isDialogOpen} fullWidth>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent>
        <Typography variant="bW500F15">{formatMessage(messages.textContent)}</Typography>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => setDialogOpen(false)} variant="text">
          {formatMessage(messages.cancel)}
        </Button>
        <Button type="button" variant="contained" color="primary" onClick={handleDeleteFlow}>
          {formatMessage(messages.confirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteDialog;
